import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

// Customer-facing payment chip — surface UNPAID / PARTIAL / PAID only.
// Lifecycle status (DRAFT/FINAL/VOID) isn't useful to customers and would
// confuse them with the payment state.
const getCustomerPaymentChip = (status) => {
  switch (status) {
    case 'PAID': return { label: 'Paid', color: 'success' };
    case 'PARTIAL': return { label: 'Partly paid', color: 'info' };
    case 'UNPAID':
    default: return { label: 'Unpaid', color: 'warning' };
  }
};

const FS = '0.8125rem';
const SECTION_TITLE_SX = { fontSize: '0.875rem', fontWeight: 600, mb: 1.5 };
const LABEL_SX = { fontSize: '0.75rem', color: 'text.secondary' };
const VALUE_SX = { fontSize: FS, fontWeight: 500 };
const BTN_SX = { textTransform: 'none', fontSize: FS };

const Section = ({ title, children, sx }) => (
  <Paper sx={{ p: 2, ...sx }}>
    {title && <Typography sx={SECTION_TITLE_SX}>{title}</Typography>}
    {children}
  </Paper>
);

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Box sx={VALUE_SX}>{children}</Box>
  </Box>
);

const TotalRow = ({ label, value, strong, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, py: 0.25 }}>
    <Typography sx={{ fontSize: FS, fontWeight: strong ? 600 : 400, color: strong ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: strong ? '0.875rem' : FS, fontWeight: strong ? 700 : 500, color: color || (strong ? 'primary.main' : 'text.primary') }}>{value}</Typography>
  </Box>
);

const formatCurrency = (value) => `AED ${parseFloat(value || 0).toFixed(2)}`;

const PortalInvoiceDetail = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { invoiceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.getPortalInvoice(invoiceId);
      setInvoice(response.data?.data || null);
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
      setError('Failed to load invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await ApiService.getPortalInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={<Button size="small" onClick={fetchInvoice} sx={BTN_SX}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }

  if (!invoice) {
    return (
      <Alert severity="warning">
        Invoice not found
      </Alert>
    );
  }

  const lineItems = invoice.line_items || invoice.lineItems || [];
  const amountDue = parseFloat(invoice.amount_due || 0);

  const paymentChip = getCustomerPaymentChip(invoice.customer_payment_status);
  const canPay = amountDue > 0 && invoice.status !== 'DRAFT' && invoice.status !== 'VOID';

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Tooltip title="Back to Invoices">
          <IconButton size="small" onClick={() => navigate('/portal/invoices')}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{invoice.invoice_number}</Typography>
        <Chip label={paymentChip.label} color={paymentChip.color} size="small" />
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
          {invoice.status !== 'DRAFT' && (
            <Tooltip title="Download PDF">
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  {downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <PdfIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canPay && portalFeaturesForSession(portalUser).PAYMENTS && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PaymentIcon fontSize="small" />}
              onClick={() => navigate(`/portal/payments?invoice=${invoiceId}`)}
              sx={BTN_SX}
            >
              Pay {formatCurrency(amountDue)}
            </Button>
          )}
        </Box>
      </Box>

      {/* Details + Payment Summary */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Section title="Invoice Details" sx={{ height: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Field label="Invoice Number">{invoice.invoice_number || '—'}</Field>
              <Field label="Invoice Date">{formatDate(invoice.invoice_date, '—')}</Field>
              {invoice.due_date && <Field label="Due Date">{formatDate(invoice.due_date, '—')}</Field>}
              {invoice.item_count != null && <Field label="Items">{invoice.item_count}</Field>}
              {invoice.work_order_id && (
                <Field label="Work Order">
                  <Chip
                    label={invoice.work_order_number}
                    size="small"
                    color="info"
                    onClick={() => navigate(`/portal/work-orders/${invoice.work_order_id}`)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Field>
              )}
              {invoice.quotation_id && (
                <Field label="Quotation">
                  <Chip
                    label={invoice.quotation_number}
                    size="small"
                    color="secondary"
                    onClick={() => navigate(`/portal/quotations/${invoice.quotation_id}`)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Field>
              )}
            </Box>
          </Section>
        </Grid>
        <Grid item xs={12} md={4}>
          <Section title="Payment Summary" sx={{ height: '100%' }}>
            <TotalRow label="Subtotal" value={formatCurrency(invoice.subtotal)} />
            <TotalRow label="VAT" value={formatCurrency(invoice.tax_total)} />
            <Divider sx={{ my: 0.75 }} />
            <TotalRow label="Grand Total" value={formatCurrency(invoice.grand_total)} strong />
            <TotalRow label="Amount Paid" value={formatCurrency(invoice.paid_amount)} color="success.main" />
            {amountDue > 0 && (
              <TotalRow label="Amount Due" value={formatCurrency(amountDue)} strong color="error.main" />
            )}
          </Section>
        </Grid>
      </Grid>

      {/* Line Items */}
      <Paper sx={{ mb: 2 }}>
        <Typography sx={{ ...SECTION_TITLE_SX, px: 2, pt: 1.5, mb: 1 }}>Line Items</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Govt Charge</TableCell>
                <TableCell align="right">Service Charge</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No line items</TableCell>
                </TableRow>
              ) : (
                lineItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 500 }}>{item.service_name}</Box>
                      {item.ticket_number && (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                          Ticket: {item.ticket_number}
                        </Typography>
                      )}
                      {item.is_external && (
                        <Chip label="External" size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.6875rem' }} />
                      )}
                    </TableCell>
                    <TableCell>{item.customer_name || '—'}</TableCell>
                    <TableCell align="right">{formatCurrency(item.govt_charge)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.service_charge)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.tax_value)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.line_total)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {invoice.notes && (
        <Section title="Notes">
          <Typography sx={{ fontSize: FS, whiteSpace: 'pre-line' }}>{invoice.notes}</Typography>
        </Section>
      )}
    </Box>
  );
};

export default PortalInvoiceDetail;
